/**
 * AppBanner — shows a dismissable "Open in App" banner in mobile browsers.
 *
 * Only renders when:
 *  - NOT inside Capacitor (native app already running)
 *  - NOT running as installed PWA (standalone mode)
 *  - User hasn't dismissed in the last 7 days
 *  - Device is a phone or tablet
 *
 * Banner adapts per OS:
 *  - iOS: deep-link to custom URL scheme (gameday://) — falls back gracefully
 *  - Android: intent:// link for Chrome's App Links
 *  - Unknown: direct link to app store listing
 *
 * Performance: renders null in Capacitor, zero cost.
 */
import { useState, useEffect, useRef } from 'react';

const DISMISS_KEY   = 'gameday:banner-dismissed';
const DISMISS_TTL   = 7 * 24 * 60 * 60 * 1000; // 7 days
const APP_SCHEME    = 'gameday://';
const APP_STORE_IOS = 'https://apps.apple.com/app/gameday/id0000000000'; // replace with real ID
const APP_STORE_AND = 'https://play.google.com/store/apps/details?id=app.gameday.app';
const ANDROID_INTENT = `intent://#Intent;scheme=gameday;package=app.gameday.app;end;`;

// Check if running as installed PWA
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

// Check capacitor
const isCapacitor = () =>
  !!(window.Capacitor?.isNativePlatform?.()) || !!window.Capacitor?.platform;

function wasDismissedRecently() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    return ts && Date.now() - Number(ts) < DISMISS_TTL;
  } catch { return false; }
}

function setDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

export function AppBanner() {
  const [show, setShow] = useState(false);
  const [os, setOs] = useState('unknown');
  // Store pending timeout IDs so we can cancel them if the component unmounts
  const timerRef = useRef(null);

  useEffect(() => {
    if (isCapacitor() || isStandalone()) return;
    if (wasDismissedRecently()) return;

    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(ua);

    if (!isIOS && !isAndroid) return; // desktop — don't show

    setOs(isIOS ? 'ios' : 'android');
    setShow(true);
  }, []);

  // Cancel any pending store-redirect timer when the component unmounts
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const dismiss = () => {
    setShow(false);
    setDismissed();
  };

  const openApp = () => {
    if (os === 'ios') {
      window.location.href = APP_SCHEME;
      timerRef.current = setTimeout(() => {
        window.location.href = APP_STORE_IOS;
      }, 800);
    } else {
      window.location.href = ANDROID_INTENT;
      timerRef.current = setTimeout(() => {
        window.location.href = APP_STORE_AND;
      }, 800);
    }
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      role="banner"
      aria-label="Open in Game Day app"
      style={{
        // In-flow flex item — sits between the top bar and page content.
        // position:fixed was removing it from flow and hiding the top bar behind it.
        flexShrink: 0,
        background: '#060a12',
        borderBottom: '1px solid rgba(0,212,255,0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        // No safe-area padding here — the top bar above this handles it
        // GPU layer — prevents repaints from scroll below
        transform: 'translateZ(0)',
        contain: 'layout style paint',
        boxShadow: '0 2px 16px rgba(0,0,0,0.5)',
        zIndex: 10,
      }}
    >
      {/* Close button */}
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'rgba(255,255,255,0.4)',
          padding: '4px 6px',
          borderRadius: 6,
          fontSize: 18,
          lineHeight: 1,
          minWidth: 36,
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        ✕
      </button>

      {/* Icon */}
      <div style={{
        width: 44,
        height: 44,
        borderRadius: 10,
        background: 'var(--ice-a10)',
        border: '1px solid var(--ice-a20)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 24,
        flexShrink: 0,
      }}>
        🏟
      </div>

      {/* Label */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 800,
          fontSize: 15,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: '#edf0f4',
          lineHeight: 1.2,
        }}>
          Game Day
        </div>
        <div style={{
          fontSize: 11,
          color: 'rgba(255,255,255,0.45)',
          fontFamily: 'var(--font-body)',
          marginTop: 2,
        }}>
          Better experience in the app
        </div>
      </div>

      {/* CTA */}
      <button
        onClick={openApp}
        style={{
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
          background: 'var(--ice)',
          color: '#000',
          border: 'none',
          cursor: 'pointer',
          borderRadius: 8,
          fontFamily: 'var(--font-display)',
          fontWeight: 800,
          fontSize: 13,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          padding: '8px 16px',
          minHeight: 36,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {os === 'ios' ? 'Open' : 'Get App'}
      </button>
    </div>
  );
}
